import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../../api/adminApi';
import {
  ADMIN_ROLES, PLATFORM_ROLES, TENANT_ROLES, PLATFORM_TENANT_ID, PUBLIC_TENANT, isPlatformRole, type AdminRole,
} from '../../../config/adminRoles';
import type { AdminUserRow, RoleGrant } from '../../../api/userApi';
import { tenantsApi } from '../../../api/tenantsApi';
import './AdminUsers.css';

type AdminUser = AdminUserRow;

// Accept the broader UserRole[] (may include 'student'/'teacher') and filter
// down to admin-only display values.
function formatRoles(roles: readonly string[] | undefined): string {
  // No implicit default any more — the backend requires an explicit role, since
  // the old `tenant_admin` fallback would now mean authority over the whole
  // public tenant. Legacy rows with none genuinely have none.
  if (!roles || roles.length === 0) return '—';
  const adminOnly = roles.filter(r => (ADMIN_ROLES as readonly string[]).includes(r));
  return adminOnly.length === 0 ? 'tenant_admin (default)' : adminOnly.join(', ');
}

export default function AdminUsers() {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editPhoneUser, setEditPhoneUser] = useState<AdminUser | null>(null);
  const [grantsUser, setGrantsUser] = useState<AdminUser | null>(null);
  const [error, setError] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => adminApi.getAdminUsers(),
  });

  const createMutation = useMutation({
    mutationFn: (user: { name: string; email: string; password: string; phone?: string; roles?: AdminRole[] }) =>
      adminApi.createAdminUser(user),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setShowCreateModal(false);
      setError('');
    },
    onError: (err: Error) => setError(err.message),
  });

  const updatePhoneMutation = useMutation({
    mutationFn: ({ userId, phone }: { userId: string; phone: string }) =>
      adminApi.updateAdminUser(userId, { phone }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setEditPhoneUser(null);
      setError('');
    },
    onError: (err: Error) => setError(err.message),
  });

  const { data: tenantData } = useQuery({ queryKey: ['tenants'], queryFn: () => tenantsApi.list() });
  const tenantName = (scope: string): string => {
    if (scope === PLATFORM_TENANT_ID) return 'platform';
    if (scope === PUBLIC_TENANT) return 'public';
    return tenantData?.tenants.find(t => t.id === scope)?.name ?? scope;
  };

  const grantsMutation = useMutation({
    mutationFn: ({ userId, assignments }: { userId: string; assignments: RoleGrant[] }) =>
      adminApi.setAdminRoleAssignments(userId, assignments),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setGrantsUser(null);
      setError('');
    },
    onError: (err: Error) => setError(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (userId: string) => adminApi.deleteAdminUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const handleDelete = (user: AdminUser) => {
    if (window.confirm(`Delete admin user "${user.name}" (${user.email})?`)) {
      deleteMutation.mutate(user.id);
    }
  };

  return (
    <div className="admin-users-page">
      <h2>Platform Admins</h2>

      {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="admin-users-actions">
        <button onClick={() => { setError(''); setShowCreateModal(true); }}>
          + Create Admin User
        </button>
      </div>

      {isLoading ? (
        <p>Loading...</p>
      ) : !data?.users?.length ? (
        <div className="empty-state">No admin users yet. Create one to get started.</div>
      ) : (
        <table className="dt dt--compact">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Grants</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.users.map((user: AdminUser) => (
              <tr key={user.id}>
                <td>{user.name}</td>
                <td>{user.email}</td>
                <td>{user.phone || <span style={{ color: '#888' }}>—</span>}</td>
                <td style={{ fontSize: 12 }}>
                  {user.roleAssignments?.length ? (
                    <div className="grant-chips">
                      {user.roleAssignments.map(a => (
                        <span key={`${a.role}@${a.scope}`} className="grant-chip">
                          {a.role} <span className="grant-chip-scope">@ {tenantName(a.scope)}</span>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span style={{ color: '#888' }}>
                      {formatRoles((user.roles ?? []).filter(r => (ADMIN_ROLES as readonly string[]).includes(r)) as AdminRole[])}
                    </span>
                  )}
                </td>
                <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                <td className="actions-cell">
                  <button onClick={() => { setError(''); setEditPhoneUser(user); }}>
                    {user.phone ? 'Edit Phone' : 'Set Phone'}
                  </button>
                  {!user.roles?.includes('super_admin') && (
                    <button onClick={() => { setError(''); setGrantsUser(user); }}>Grants</button>
                  )}
                  {/* The synthetic super-admin (env-driven, marked by 'super_admin'
                      in roles) is not deletable — it backs platform login. */}
                  {!user.roles?.includes('super_admin') && (
                    <button className="danger" onClick={() => handleDelete(user)}>
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Create Admin User Modal */}
      {showCreateModal && (
        <CreateAdminUserModal
          onClose={() => { setShowCreateModal(false); setError(''); }}
          onSubmit={(user) => createMutation.mutate(user)}
          isLoading={createMutation.isPending}
          error={error}
        />
      )}

      {grantsUser && (
        <GrantsModal
          user={grantsUser}
          tenants={(tenantData?.tenants ?? []).filter(t => t.id !== PLATFORM_TENANT_ID && t.id !== PUBLIC_TENANT)}
          onClose={() => { setGrantsUser(null); setError(''); }}
          onSubmit={(assignments) => grantsMutation.mutate({ userId: grantsUser.id, assignments })}
          isLoading={grantsMutation.isPending}
          error={error}
        />
      )}

      {/* Edit Phone Modal */}
      {editPhoneUser && (
        <EditPhoneModal
          user={editPhoneUser}
          onClose={() => { setEditPhoneUser(null); setError(''); }}
          onSubmit={(phone) =>
            updatePhoneMutation.mutate({ userId: editPhoneUser.id, phone })
          }
          isLoading={updatePhoneMutation.isPending}
          error={error}
        />
      )}
    </div>
  );
}

function CreateAdminUserModal({
  onClose,
  onSubmit,
  isLoading,
  error,
}: {
  onClose: () => void;
  onSubmit: (user: { name: string; email: string; password: string; phone?: string; roles?: AdminRole[] }) => void;
  isLoading: boolean;
  error: string;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [roles, setRoles] = useState<AdminRole[]>([]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedPhone = phone.trim();
    onSubmit({
      name,
      email,
      password,
      phone: trimmedPhone ? trimmedPhone : undefined,
      roles,
    });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h3>Create Admin User</h3>
        {error && <div className="error-message" style={{ marginBottom: 12 }}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Phone (optional, E.164 — used for WhatsApp alerts)</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+919876543210"
              pattern="\+[0-9]{7,15}"
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
            />
          </div>
          <div className="form-group">
            <label>Roles</label>
            <RoleCheckboxList value={roles} onChange={setRoles} />
            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
              At least one role is required. Tenant roles (tenant, user, content) apply to
              the public tenant — the consumer business and the shared content library.
              Platform roles (prompt, eval, ops) apply to the platform.
            </div>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={isLoading || roles.length === 0}>
              {isLoading ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RoleCheckboxList({ value, onChange }: { value: AdminRole[]; onChange: (next: AdminRole[]) => void }) {
  const toggle = (role: AdminRole) => {
    onChange(value.includes(role) ? value.filter(r => r !== role) : [...value, role]);
  };
  return (
    <div className="role-checkbox-list">
      {ADMIN_ROLES.map(role => (
        <label key={role}>
          <input
            type="checkbox"
            checked={value.includes(role)}
            onChange={() => toggle(role)}
          />
          <span className="role-name">{role}</span>
        </label>
      ))}
    </div>
  );
}

function EditPhoneModal({
  user,
  onClose,
  onSubmit,
  isLoading,
  error,
}: {
  user: AdminUser;
  onClose: () => void;
  onSubmit: (phone: string) => void;
  isLoading: boolean;
  error: string;
}) {
  const [phone, setPhone] = useState(user.phone ?? '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(phone.trim());
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h3>Set Phone for {user.name}</h3>
        {error && <div className="error-message" style={{ marginBottom: 12 }}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Phone (E.164 — e.g. +919876543210). Leave empty to clear.</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+919876543210"
              pattern="\+[0-9]{7,15}"
              autoFocus
            />
            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
              Used to deliver WhatsApp alerts to this admin.
            </div>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


/**
 * Scoped grants for one admin: each row is a role and where it applies.
 * Platform roles apply only at the platform; tenant roles at the public tenant
 * or a customer tenant. Granting a tenant role at another tenant is how a
 * staff member is given one school's users without moving their membership.
 */
function GrantsModal({
  user,
  tenants,
  onClose,
  onSubmit,
  isLoading,
  error,
}: {
  user: AdminUser;
  tenants: { id: string; name: string; status: string }[];
  onClose: () => void;
  onSubmit: (assignments: RoleGrant[]) => void;
  isLoading: boolean;
  error: string;
}) {
  const [rows, setRows] = useState<RoleGrant[]>(user.roleAssignments ?? []);
  const scopesFor = (role: AdminRole): { id: string; label: string }[] =>
    isPlatformRole(role)
      ? [{ id: PLATFORM_TENANT_ID, label: 'Platform' }]
      : [
          { id: PUBLIC_TENANT, label: 'Public (consumer + shared library)' },
          ...tenants.map(t => ({ id: t.id, label: t.status === 'active' ? t.name : `${t.name} (${t.status})` })),
        ];
  const defaultScope = (role: AdminRole) => (isPlatformRole(role) ? PLATFORM_TENANT_ID : PUBLIC_TENANT);

  const update = (i: number, next: RoleGrant) => setRows(rows.map((r, j) => (j === i ? next : r)));
  const keys = rows.map(r => `${r.role}@${r.scope}`);
  const hasDuplicate = new Set(keys).size !== keys.length;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(rows);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content grants-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Grants for {user.name}</h3>
        {error && <div className="error-message" style={{ marginBottom: 12 }}>{error}</div>}
        <form onSubmit={handleSubmit}>
          {rows.length === 0 && <p className="grants-empty">No grants — this admin will hold no authority.</p>}
          {rows.map((row, i) => (
            <div key={i} className="grant-row">
              <select
                value={row.role}
                onChange={(e) => {
                  const role = e.target.value as AdminRole;
                  // Keep the scope when it still fits the new role; otherwise reset it.
                  const fits = scopesFor(role).some(s => s.id === row.scope);
                  update(i, { role, scope: fits ? row.scope : defaultScope(role) });
                }}
              >
                <optgroup label="Tenant roles">
                  {TENANT_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </optgroup>
                <optgroup label="Platform roles">
                  {PLATFORM_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </optgroup>
              </select>
              <span className="grant-at">at</span>
              <select
                value={row.scope}
                disabled={isPlatformRole(row.role)}
                onChange={(e) => update(i, { ...row, scope: e.target.value })}
              >
                {scopesFor(row.role).map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <button type="button" className="grant-remove" onClick={() => setRows(rows.filter((_, j) => j !== i))} aria-label="Remove grant">
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn-secondary grant-add"
            onClick={() => setRows([...rows, { role: 'user_admin', scope: PUBLIC_TENANT }])}
          >
            + Add grant
          </button>
          <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
            <strong>user_admin</strong> at a tenant lets them open and edit that tenant&apos;s users;
            {' '}<strong>tenant_admin</strong> also lets them change roles there. Name, phone and
            notification email stay super-admin only. Takes effect at their next login.
          </div>
          {hasDuplicate && <div className="error-message" style={{ marginTop: 8 }}>The same grant is listed twice.</div>}
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={isLoading || hasDuplicate}>
              {isLoading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
